interface LayerControlProps {
  visibleLayers: Record<string, boolean>
  onToggleLayer: (layer: string) => void
  layerCounts: Record<string, number | undefined>
  layerLoading: Record<string, boolean>
  /** Untruncated per-layer totals from the map endpoints that truncate
   *  (meters/conductors/devices). When features.length < total, the layer row
   *  grows an "N of M shown" honesty note (spec A4/§3.7). */
  layerTotals?: Record<string, number | undefined>
  statusFilter: string | undefined
  onStatusFilterChange: (status: string | undefined) => void
  vdropNeedsFeeder?: boolean
  /** When the fault-current layer is on but no feeder is selected, prompt for
   *  one (the layer is feeder-scoped). */
  faultNeedsFeeder?: boolean
  /** When the fault-current layer is on and its data is placeholder-sourced,
   *  surface the INDICATIVE-only stamp beneath the row. */
  faultIndicative?: boolean
  /** When the secondary layer is on, toggles the color-by-confidence mode
   *  (style each segment by its derived rooting tier). */
  secondaryColorByConfidence?: boolean
  onToggleSecondaryColorByConfidence?: () => void
}

import { TIER_COLORS, NO_CONNECTIVITY_COLOR } from '../../types/secondary.ts'
import { FAULT_CURRENT_LEGEND } from '../../utils/faultCurrentStyle.ts'
import { shapeVertices, METER_SYMBOL, type ShapeKind } from './symbology.ts'

type SwatchKind = ShapeKind | 'line'

const LAYER_CONFIG: { key: string; label: string; color: string; swatch: SwatchKind }[] = [
  { key: 'substations', label: 'Substations', color: '#e74c3c', swatch: 'hexagon' },
  { key: 'feeders', label: 'Feeders', color: '#3498db', swatch: 'line' },
  { key: 'transformers', label: 'Transformers', color: '#f39c12', swatch: 'triangle' },
  { key: 'meters', label: 'Meters', color: '#95a5a6', swatch: METER_SYMBOL.shape },
  { key: 'conductors', label: 'Conductors', color: '#8B4513', swatch: 'line' },
  { key: 'secondary', label: 'Secondary', color: '#9b59b6', swatch: 'line' },
  // #4ECDC4 is the switching color (COLORS.device.switch / switch_46kv in
  // MapView.tsx), which is what a square actually renders as on the map —
  // switching devices are 1,532 of 2,101 prod devices, the family this
  // shape needs to read correctly for. #FF6B6B (fuse) renders as a diamond,
  // not a square; using it here would show a symbol the map never draws.
  { key: 'devices', label: 'Devices', color: '#4ECDC4', swatch: 'square' },
]

const ENGINEERING_LAYERS: {
  key: string
  label: string
  color: string
  title?: string
}[] = [
  {
    key: 'loading',
    label: 'Loading (measured/hybrid, est. fallback)',
    color: '#2ecc71',
    title:
      'Every transformer, colored by measured/hybrid effective loading; a flat model estimate is used only where there is no AMI.',
  },
  { key: 'voltageDrop', label: 'Voltage Drop', color: '#f1c40f' },
  { key: 'faultCurrent', label: 'Fault Current (indicative)', color: '#e74c3c' },
  {
    key: 'measuredLoading',
    label: 'AMI Measured Loading',
    color: '#a78bfa',
    title:
      'Only AMI-covered transformers from the measured-window endpoint; insufficient-data banks shown distinctly; no model numbers.',
  },
  { key: 'voltageViolations', label: 'AMI Voltage Violations', color: '#dc2626' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'verified', label: 'Verified' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'needs_review', label: 'Needs Review' },
]

export function LayerControl({
  visibleLayers,
  onToggleLayer,
  layerCounts,
  layerLoading,
  layerTotals,
  statusFilter,
  onStatusFilterChange,
  vdropNeedsFeeder,
  faultNeedsFeeder,
  faultIndicative,
  secondaryColorByConfidence,
  onToggleSecondaryColorByConfidence,
}: LayerControlProps) {
  return (
    <div className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-md p-3 min-w-48">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Layers
      </h3>
      <div className="space-y-1">
        {LAYER_CONFIG.map(({ key, label, color, swatch }) => (
          <div key={key}>
            <label
              className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:bg-gray-50 rounded px-1 py-0.5"
            >
              <input
                type="checkbox"
                checked={visibleLayers[key] ?? false}
                onChange={() => onToggleLayer(key)}
                className="rounded border-gray-300"
              />
              <Swatch kind={swatch} color={color} />
              <span className="flex-1">{label}</span>
              {layerLoading[key] && (
                <span className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              )}
              {!layerLoading[key] && layerCounts[key] !== undefined && (
                <span className="text-xs text-gray-400">{layerCounts[key]}</span>
              )}
            </label>
            {/* Truncation honesty (spec A4/D-6): when the server truncated the
                layer, say so — never render a silently-partial layer as
                complete. */}
            {!layerLoading[key] && (
              <TruncationNote shown={layerCounts[key]} total={layerTotals?.[key]} />
            )}
            {/* Secondary color-by-confidence sub-toggle + legend (mirrors the
                voltageDrop contextual control). */}
            {key === 'secondary' && visibleLayers.secondary && onToggleSecondaryColorByConfidence && (
              <div className="ml-6 mt-0.5 mb-0.5">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600 hover:text-gray-800">
                  <input
                    type="checkbox"
                    checked={secondaryColorByConfidence ?? false}
                    onChange={onToggleSecondaryColorByConfidence}
                    className="rounded border-gray-300"
                  />
                  <span>Color by confidence</span>
                </label>
                {secondaryColorByConfidence && <ConfidenceLegend />}
              </div>
            )}
            {key === 'devices' && visibleLayers.devices && (
              <div className="ml-6 mt-0.5 mb-0.5">
                <DeviceKey />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Engineering layers */}
      <div className="mt-3 pt-2 border-t border-gray-200">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Engineering
        </h3>
        <div className="space-y-1">
          {ENGINEERING_LAYERS.map(({ key, label, color, title }) => (
            <div key={key}>
              <label
                title={title}
                className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:bg-gray-50 rounded px-1 py-0.5"
              >
                <input
                  type="checkbox"
                  checked={visibleLayers[key] ?? false}
                  onChange={() => onToggleLayer(key)}
                  className="rounded border-gray-300"
                />
                <Swatch
                  kind={key === 'voltageViolations' ? 'circle' : key === 'loading' || key === 'measuredLoading' ? 'triangle' : 'line'}
                  color={color}
                />
                <span className="flex-1">{label}</span>
                {layerLoading[key] && (
                  <span className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                )}
                {!layerLoading[key] && layerCounts[key] !== undefined && (
                  <span className="text-xs text-gray-400">{layerCounts[key]}</span>
                )}
              </label>
              {key === 'voltageDrop' && visibleLayers.voltageDrop && vdropNeedsFeeder && (
                <div className="text-xs text-amber-600 ml-6 mt-0.5">Click a feeder first</div>
              )}
              {/* Fault-current layer: feeder-scoped prompt + INDICATIVE stamp +
                  kA legend (mirrors the voltageDrop contextual pattern). */}
              {key === 'faultCurrent' && visibleLayers.faultCurrent && (
                <div className="ml-6 mt-0.5 mb-0.5">
                  {faultNeedsFeeder && (
                    <div className="text-xs text-amber-600">Click a feeder first</div>
                  )}
                  {!faultNeedsFeeder && faultIndicative && (
                    <div className="text-[11px] text-amber-600">
                      Placeholder source data — magnitudes indicative until utility
                      data loaded
                    </div>
                  )}
                  <FaultCurrentLegend />
                </div>
              )}
              {/* Measured loading colors by EFFECTIVE load: hybrid banks (some
                  silent meters) fold a flat per-silent-meter estimate into the
                  measured peak, so the dot reflects the conservative basis. */}
              {key === 'measuredLoading' && visibleLayers.measuredLoading && (
                <div className="text-[11px] text-gray-500 ml-6 mt-0.5">
                  Colored by effective load; hybrid banks add ~5 kW per silent
                  meter to the measured peak.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Verification status filter */}
      <div className="mt-3 pt-2 border-t border-gray-200">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
          Status Filter
        </label>
        <select
          value={statusFilter ?? ''}
          onChange={e => onStatusFilterChange(e.target.value || undefined)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  )
}

/** "10,000 of 10,227 shown — zoom in to narrow" under a truncated layer
 *  (features.length < total_count). Renders nothing when the collection is
 *  complete or the endpoint doesn't report a total. */
function TruncationNote({
  shown,
  total,
}: {
  shown: number | undefined
  total: number | undefined
}) {
  if (shown == null || total == null || shown >= total) return null
  return (
    <div className="text-[11px] text-amber-600 ml-6 mt-0.5">
      {shown.toLocaleString()} of {total.toLocaleString()} shown — zoom in to narrow
    </div>
  )
}

/** Legend for the fault-current layer's kA color buckets (hot near the source,
 *  cool at line ends, gray for islanded / no geometry). */
function FaultCurrentLegend() {
  return (
    <div className="mt-1 space-y-0.5">
      {FAULT_CURRENT_LEGEND.map(({ color, label }) => (
        <div key={label} className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span
            className="w-3 h-0.5 inline-block shrink-0 rounded"
            style={{ backgroundColor: color }}
          />
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}

/** Legend for the secondary color-by-confidence mode. Tiers mirror the derived
 *  rooting confidence (auto/flagged/review) plus a no-data state. */
function ConfidenceLegend() {
  const items: { color: string; label: string }[] = [
    { color: TIER_COLORS.auto, label: 'Auto (≥0.90)' },
    { color: TIER_COLORS.flagged, label: 'Flagged (0.70–0.90)' },
    { color: TIER_COLORS.review, label: 'Review / unrooted' },
    { color: NO_CONNECTIVITY_COLOR, label: 'No data' },
  ]
  return (
    <div className="mt-1 space-y-0.5">
      {items.map(({ color, label }) => (
        <div key={label} className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span
            className="w-3 h-0.5 inline-block shrink-0 rounded"
            style={{ backgroundColor: color }}
          />
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}

/** Legend swatch drawn from the same geometry the map uses, so the legend
 *  cannot drift from the symbols again. */
function Swatch({
  kind,
  color,
  hollow = false,
}: {
  kind: SwatchKind
  color: string
  hollow?: boolean
}) {
  if (kind === 'line') {
    return (
      <span
        className="w-3 h-0.5 inline-block shrink-0 rounded"
        style={{ backgroundColor: color }}
      />
    )
  }
  const fill = hollow ? 'none' : color
  const strokeWidth = hollow ? 2 : 1
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      className="inline-block shrink-0"
      aria-hidden="true"
    >
      {kind === 'circle' ? (
        <circle cx="6" cy="6" r="4.5" fill={fill} stroke={color} strokeWidth={strokeWidth} />
      ) : (
        <polygon
          points={shapeVertices(kind, 6, 6, 5)
            .map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`)
            .join(' ')}
          fill={fill}
          stroke={color}
          strokeWidth={strokeWidth}
        />
      )}
    </svg>
  )
}

/** Passive key for the Devices layer — shapes only, no checkboxes and no new
 *  visibility state (per-family filtering is deliberately out of scope).
 *  Mirrors the ConfidenceLegend / FaultCurrentLegend pattern above. */
function DeviceKey() {
  const items: { swatch: SwatchKind; color: string; label: string; hollow?: boolean }[] = [
    { swatch: 'square', color: '#4ECDC4', label: 'Switching (switch, 46 kV)' },
    { swatch: 'square', color: '#95E1D3', label: 'Sectionalizer' },
    // Fuse and recloser stay grouped under the fuse color (#FF6B6B) — accepted
    // caveat: this is only accurate because prod currently has zero reclosers
    // (recloser's true map color is #FFE66D) and will drift the day one is added.
    { swatch: 'diamond', color: '#FF6B6B', label: 'Interrupting (fuse, recloser)' },
    { swatch: 'triangleDown', color: '#DDA0DD', label: 'Capacitor' },
    { swatch: 'square', color: '#87CEEB', label: 'Open / tie point', hollow: true },
  ]
  return (
    <div className="mt-1 space-y-0.5">
      {items.map(({ swatch, color, label, hollow }) => (
        <div key={label} className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <Swatch kind={swatch} color={color} hollow={hollow} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}
